import React, { useCallback, useContext } from 'react';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { Grid } from '@mui/material';
import { Button, Card, TextField, Typography } from '@totalsoft/rocket-ui';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { get } from '@totalsoft/react-state-lens';
import useConfirmDialog from 'features/common/hooks/useConfirmDialog';
import { useAuthorization } from 'hooks';
import InsuranceDataContext from 'features/insuranceDocument/stepper/insuranceDataContext';
import AuthorizationCodes from 'features/common/constants/authorizationCodes';
import { CardActionsContainer } from 'components/common/CardStyles';

const CommentComponent = ({ commentLens, processing, onSave, onChange, onDelete, validation, readOnly }) => {
  const { documentTypeId, documentStateId } = useContext(InsuranceDataContext);
  const hasRightToDeleteComment = useAuthorization(AuthorizationCodes.hasRightToDeleteComment, documentTypeId, documentStateId);
  const { t } = useTranslation();
  const confirm = useConfirmDialog();
  const isNew = (commentLens.isNew |> get) || false;

  const handleDelete = useCallback(() => {
    if (isNew) {
      onDelete();
      return;
    }
    confirm(() => onDelete(commentLens.documentCommentId |> get), { title: t('Offer.Edit.Comments.ConfirmDelete') });
  }, [isNew, commentLens, confirm, onDelete, t]);

  return (
    <Card
      title={
        <Typography variant='subtitle2'>
          <strong>{commentLens.userName |> get}</strong>
          {t('Offer.Edit.Comments.AddedOn') +
            t('DATE_FORMAT', {
              date: { value: commentLens.internalTime |> get, format: 'DD/MMM/YY HH:mm:ss' }
            }) +
            t('Offer.Edit.Comments.Status')}
          <strong>{commentLens.stateName |> get}</strong>
        </Typography>
      }
    >
      {!isNew && <Typography>{commentLens.freeTxt |> get}</Typography>}
      {isNew && (
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <TextField
              id={'newComment'}
              fullWidth
              value={commentLens.freeTxt |> get}
              onChange={onChange}
              error={!isValid(validation.freeTxt)}
              helperText={getErrors(validation.freeTxt)}
              inputProps={{
                maxLength: 8000
              }}
            />
          </Grid>
        </Grid>
      )}
      <CardActionsContainer mt={2}>
        {isNew && (
          <Button id={'saveCommentBtn'} onClick={onSave} disabled={processing || readOnly}>
            {processing ? t('General.Adding') : t('General.Add')}
          </Button>
        )}
        <Button id={'saveCommentBtn'} onClick={handleDelete} disabled={processing || !hasRightToDeleteComment}>
          {t('Offer.Edit.Comments.Delete')}
        </Button>
      </CardActionsContainer>
    </Card>
  );
};

CommentComponent.propTypes = {
  commentLens: PropTypes.object.isRequired,
  processing: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  validation: PropTypes.object.isRequired,
  readOnly: PropTypes.bool.isRequired
};

export default CommentComponent;
